import * as React from 'react';
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {PluginTemplates} from "../../../../utils/PluginTemplates";
import {lazyInject} from "@athena-portal/core";
import {
    Party,
    Policy,
    POLICY_STATUS_MTC,
    PolicyPropertyEnum,
    Renewal,
    User,
} from "@ferry/journey-services";
import {
    FalconNavigatorHeader,
    resourcesHelper,
    Footer,
} from "@ferry/portal-core";
import FalconHeader from "../../../../components/navigation/FalconHeader";
import {
    AutomaticRenewalCheckboxSection
} from "../../common/payments/paymentDetails/annual/AutomaticRenewalCheckboxSection";
import * as moment from "moment/moment";
import {AccountDetailsBanner} from "../../../../components/banners/AccountDetailsBanner";
import {PersonalDetailsWidget} from "../../../../components/accountDetails/PersonalDetailsWidget";
import {ContactDetailsWidget} from "../../../../components/accountDetails/ContactDetailsWidget";
import {ChangePasswordWidget} from "../../../../components/accountDetails/ChangePasswordWidget";
import {AddressDetailsWidget} from "../../../../components/accountDetails/AddressDetailsWidget";
import type {ICustomerPortalService} from "../../../../services/ICustomerPortalService";
import {Types} from "../../../../Types";

interface IParams {
    policyNumber: string;
    [key: string]: string | undefined
}

interface IState {
    party?: Party;
    user?: User;
    policy?: Policy;
    mtaOnEndDateOfPolicy: boolean;
    automaticRenewal: boolean;
    performRenewal: boolean;
    renewalProposal?: Renewal;
    renewalProposalLoaded: boolean;
}

@authorize
export class AnnualAccountDetailsPagePlain extends React.Component<IWithRouterProps<IParams>, IState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.state = {
            mtaOnEndDateOfPolicy: false,
            automaticRenewal: false,
            performRenewal: false,
            renewalProposalLoaded: false
        }
    }

    public componentDidMount() {
        {window.scrollTo(0, 0)}

        this.customerPortalService
            .getPolicy(this.props.params.policyNumber)
            .subscribe((policy: Policy) => {
                policy.policyProperties.forEach(property => {
                    if (property.key === PolicyPropertyEnum.AUTOMATIC_RENEWAL) {
                        this.setState({automaticRenewal: property.value === "true"})
                    } else if (property.key === PolicyPropertyEnum.PERFORM_RENEWAL) {
                        this.setState({performRenewal: property.value === "true"})
                    }
                })

                this.customerPortalService.getRenewalProposal(this.props.params.policyNumber)
                    .subscribe((renewalProposal: Renewal) => {
                        this.setState({
                            renewalProposal,
                            renewalProposalLoaded: true
                        })
                    });

                this.setState({
                    policy,
                    mtaOnEndDateOfPolicy: moment.utc(policy.endDate).isSame(moment.utc(policy.annualPolicyInfo.maxFutureMtaDate), 'day')
                });
        });

        this.customerPortalService.getProfile().subscribe((output: Party) => {
            const party: Party = output;

            this.setState({
                party
            })
        });

        this.customerPortalService.getUserById().subscribe((user: User) => {
            this.setState({
                user
            });
        });

        this.pluginTemplates.setPageTitle("Your account details");
    }


    public render(): React.ReactNode {
        const policy = this.state.policy
        const policyIsActive = moment(policy?.endDate).isSameOrAfter(moment()) && policy?.status !== "MTC";
        const loading: boolean = !(this.state.party && this.state.policy && this.state.user && this.state.renewalProposalLoaded);

        if (loading) {
            return  <Overlay show={true}/>;
        } else if (this.state.party && this.state.user && this.state.policy && this.state.policy.annualPolicyInfo) {
            return (
                <>
                    <FalconHeader/>
                    <div className="plugin account-details-container">
                        <FalconNavigatorHeader
                            back="/dashboard"
                            title="Account details"
                        />
                        <AccountDetailsBanner/>
                        <div className="plugin plugin-panel inner-container inner-content text-center account-details-section">
                            <div className="panel-detail-output-entity account-details-title p-0 icon-container">
                                <div>
                                    <img className="account-details-icon person-icon" alt="make-changes" src={`/${resourcesHelper.THEME_PATH}/img/account-details.svg`}/>
                                </div>
                            </div>
                            <div>
                                <PersonalDetailsWidget party={this.state.party}/>
                                <div className="w-100">
                                    <img className="mt-3 mb-3 mw-100" src="/img/divider-line.svg" alt="Divider icon" aria-label="Divider line"/>
                                </div>
                                <ContactDetailsWidget party={this.state.party}/>
                                <div className="w-100">
                                    <img className="mt-3 mb-3 mw-100" src="/img/divider-line.svg" alt="Divider icon" aria-label="Divider line"/>
                                </div>
                                <AddressDetailsWidget addressChangeUrl={`/journey/show/product/AnnualMotorInsurance/process/mtaChangeAddress/preset/${this.state.policy?.interaction._id}`}
                                                      allowAddressChange={!this.state.policy?.annualPolicyInfo.hasUnpaidChargebackInvoice}
                                                      futureMta={this.state.policy?.annualPolicyInfo.maxMtaDateReached}
                                                      mtaOnEndDateOfPolicy={this.state.mtaOnEndDateOfPolicy}
                                                      fullAddress={this.state.policy?.interaction.policyHolder.address.fullAddress}
                                                      coverCancelled={this.state.policy?.status === POLICY_STATUS_MTC}
                                />
                                <div className="w-100">
                                    <img className="mt-3 mb-3 mw-100" src="/img/divider-line.svg" alt="Divider icon" aria-label="Divider line"/>
                                </div>
                                {this.state.user && <ChangePasswordWidget/>}
                            </div>
                        </div>

                        {policyIsActive &&
                            <AutomaticRenewalCheckboxSection
                                navigate={this.props.navigate}
                                activeAnnualPolicy={policy}
                                automaticRenewal={this.state.automaticRenewal}
                                performRenewal={this.state.performRenewal}
                                twoDaysGracePeriod={this.state.policy.annualPolicyInfo.inGracePeriod}
                                renewalProposal={this.state.renewalProposal}
                            />
                        }
                        {this.pluginTemplates.renderBackLink("Back", false, `/dashboard/`)}
                    </div>
                    <Footer/>
                </>
            )
        }

        return <></>
    }
}

export const AnnualAccountDetailsPage = withRouter<{}, IParams>(AnnualAccountDetailsPagePlain)
